/**
 * The questions the appointment screens ask of a list of appointments: how
 * long one runs, whether it has happened yet, which of them are worth putting
 * in front of somebody, and how each is written down.
 *
 * Here rather than in the tab because three components ask them — the banner
 * over the tabs, the carousel under them and the table under that — and three
 * components each deciding for themselves what "today" means is how a screen
 * ends up saying an appointment is today in one place and tomorrow in another.
 */

import { isStandingStatus } from "./types/appointments.types";
import type {
  AppointmentState,
  AppointmentTabId,
  ClientAppointment,
} from "./types/appointments.types";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * The weekday names, for `dayFull` alone.
 *
 * My task keeps a list of its own for the week strip. Two short arrays rather
 * than one shared constant, deliberately: the strip's and this one's are the
 * same seven words today and are answering different questions — one is a
 * column heading, the other is the front of a sentence — and a module that
 * exists to hold the names of the days is a dependency between two features
 * that otherwise have none.
 */
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** How far ahead the carousel looks. See `upcomingSoon`. */
export const CAROUSEL_WINDOW_DAYS = 7;

/** Midnight, so two moments compare on the day rather than to the second. */
const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * Whole days from one day to another, counted between midnights.
 *
 * Between midnights rather than by dividing the gap in milliseconds: an
 * appointment at 11pm tonight and one at 1am tomorrow are twenty-six hours
 * apart but one day apart, and "in 1 day" is what a reader means by it. The
 * arithmetic also survives a daylight-saving shift, which a fixed 86,400,000
 * does not — India has none, but the rounding is free.
 */
export const daysBetween = (from: Date, to: Date): number =>
  Math.round(
    (atMidnight(to).getTime() - atMidnight(from).getTime()) / 86_400_000
  );

/**
 * Whether this appointment has begun.
 *
 * Strictly after the minute it starts on, so an appointment at noon counts as
 * begun from 12:01 rather than at 12:00 sharp, when it is only beginning — the
 * row must not change under somebody in the first seconds of a call they are
 * about to join.
 *
 * This used to be the whole rule for when a screen asked what became of an
 * appointment, and it is not any more: a session is asked about once it is
 * *over*, not once it has begun — see `hasEnded`. Asking at 12:01 about a
 * two-hour session put the question in front of somebody who was still in the
 * room, and on a day's timeline it took the current hour off the screen a
 * minute after it started.
 */
export const hasStarted = (
  appointment: ClientAppointment,
  now: Date = new Date()
): boolean => {
  const start = new Date(appointment.startsAt).getTime();
  if (Number.isNaN(start)) return false;
  // A whole minute past, so the row does not change under somebody in the
  // first seconds of the appointment they are about to join.
  return now.getTime() >= start + 60_000;
};

/**
 * The shortest appointment this practice can book, in minutes — see
 * `DURATION_OPTIONS`, whose first row this is.
 *
 * Stood in for the length of a row that arrived without one. A backend record
 * with no `ends_at` comes through as a duration of 0 (see `toDurationMinutes`),
 * and taking that at its word would mean the appointment ended at the instant
 * it began: the screen would ask how it went the moment it started, which is
 * the one thing these rules exist to prevent. Fifteen minutes is the shortest
 * thing it could have been, so it is the earliest the question can be fair.
 */
const ASSUMED_MINUTES = 15;

/**
 * The moment the appointment stops running, for the purposes of deciding what
 * a screen offers — its start plus its length, or plus `ASSUMED_MINUTES`
 * where it has none. `null` where the start will not parse.
 *
 * Deliberately not `endsAtOf`, which is what the Meeting dialog prints. That
 * one has to stay honest: a row with no length has no end to write down, and
 * inventing a quarter of an hour there would put a time on screen that nobody
 * agreed to. This one is never shown — it only decides whether a question is
 * due yet — so erring late is free and erring early is not.
 */
const runsUntil = (appointment: ClientAppointment): number | null => {
  const start = new Date(appointment.startsAt).getTime();
  if (Number.isNaN(start)) return null;

  const minutes =
    appointment.durationMinutes > 0
      ? appointment.durationMinutes
      : ASSUMED_MINUTES;

  return start + minutes * 60_000;
};

/**
 * Whether the appointment is over by the clock.
 *
 * The rule behind every "what became of this?" in the app, and the reason it
 * is the end rather than the start: for as long as a session is running it is
 * a session, not a question. A practitioner halfway through a two-hour sitting
 * does not want a tick and a cross on the row — they want the way back into
 * it. The question is due the moment the hour is up, and not a minute before.
 *
 * No grace period, unlike `hasStarted`. The end has to be exactly where
 * `isInProgress` stops, or there would be a stretch of time in which the row
 * was neither running nor finished.
 */
export const hasEnded = (
  appointment: ClientAppointment,
  now: Date = new Date()
): boolean => {
  const end = runsUntil(appointment);
  return end !== null && now.getTime() >= end;
};

/**
 * Whether the row is waiting on the practitioner to say what happened: booked,
 * finished, and nobody has answered yet.
 */
export const isAwaitingAnswer = (
  appointment: ClientAppointment,
  now: Date = new Date()
): boolean =>
  isStandingStatus(appointment.status) && hasEnded(appointment, now);

/**
 * Whether the appointment is still standing: booked, not yet over, and nobody
 * has said otherwise.
 *
 * "Not yet over" rather than "not yet begun" {EM} an appointment that is running
 * right now is still one of these, and everything this gates (the join link,
 * the row menu, the Upcoming tab) should go on being offered while it is. It
 * stops being offered when the session does.
 *
 * The question behind every act the screen offers on a booking — joining it,
 * moving it, calling it off — and the reason it is a function rather than a
 * comparison at each call site. Asked of the record there is one thing to get
 * right; asked of `appointmentState` there would be the clock's "Awaiting" to
 * remember not to count, at three call sites.
 *
 * "Booked" is `isStandingStatus`, not `=== "Scheduled"`: a moved appointment
 * comes back `Rescheduled` and is every bit as joinable.
 */
export const isUpcoming = (
  appointment: ClientAppointment,
  now: Date = new Date()
): boolean =>
  isStandingStatus(appointment.status) && !hasEnded(appointment, now);

/**
 * Where a row stands: its stored status, with the clock and the record's own
 * history folded in.
 *
 * `now` is passed rather than read, so every caller on one render agrees about
 * when now is and so the suites can state it.
 */
export const appointmentState = (
  appointment: ClientAppointment,
  now: Date = new Date()
): AppointmentState => {
  // The two that were answered say themselves; nothing about the clock can
  // argue with a practitioner who has told the screen what happened.
  if (!isStandingStatus(appointment.status)) return appointment.status;

  // Booked, and over with nobody saying what became of it. Not "Completed"
  // — that is something somebody states, because a client who did not turn up
  // leaves a past appointment that did not happen and the clock cannot tell
  // the two apart. Measured from the end: a session still running is not a
  // question yet. See `hasEnded`.
  if (hasEnded(appointment, now)) return "Awaiting";

  // Still to come, or happening now, so the pill is the status itself —
  // "Scheduled", or the "Rescheduled" the backend sends for a slot that has
  // already shifted.
  return appointment.status;
};

/**
 * "30 min", "1 hr", "1 hr 30 min" — the way the table's Duration column and
 * the strips above it write a length.
 *
 * Hours first and only when there are whole ones, minutes only when there is a
 * remainder, so an hour is "1 hr" rather than "1 hr 0 min". A duration of
 * nothing — which a backend can send and a dialog cannot produce — reads as a
 * dash, since "0 min" would look like a length rather than a gap.
 */
export const durationLabel = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "-";

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (!hours) return `${rest} min`;
  if (!rest) return `${hours} hr`;
  return `${hours} hr ${rest} min`;
};

/** "26 Sep 2026", the table's first line. */
export const appointmentDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
};

/** "26 Sep" — the same day without the year, for the strips that are about now. */
export const appointmentShortDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;
};

/**
 * "Thu, 26 Sep 2026" — the whole day, spelled out, as the Meeting dialog's
 * Date row writes it.
 *
 * The weekday is on it and is the reason this is not `appointmentDate`. The
 * table's first line sits in a column of dates being scanned, where the day of
 * the week is noise; the dialog's row is a single date being read, and "Thu"
 * is the half of it somebody actually plans around.
 *
 * Takes a `Date` rather than the ISO string its neighbours take, because both
 * of its callers already hold one — the dialog has parsed it to check the
 * clock, and My task's empty state is about a day that was never an
 * appointment.
 */
export const dayFull = (date: Date): string =>
  `${DAYS_SHORT[date.getDay()]}, ${date.getDate()} ${
    MONTHS_SHORT[date.getMonth()]
  } ${date.getFullYear()}`;

/**
 * Who the appointment is with, named, whichever of the two they are.
 *
 * Every screen that only wants the words reads this rather than picking a
 * field: a row carries `clientName` or `inquiryName` and never both, and a
 * component written against one of them silently draws "Unnamed client" over
 * half the diary the day enquiries start being booked.
 *
 * `null` where the response named nobody — which is every row off a client's
 * own screen, since the list it came from was asked for by that client and
 * has nobody to name that the screen does not already know.
 */
export const attendeeName = (
  appointment: ClientAppointment
): string | null =>
  (appointment.subject === "inquiry"
    ? appointment.inquiryName
    : appointment.clientName) || null;

/**
 * The client's `public_id` where the appointment is with a client, and `null`
 * where it is with an enquiry.
 *
 * The guard in front of everything that takes an appointment and goes looking
 * for the client behind it — the Meeting dialog's contact line, and the cache
 * holding that client's own history. An enquiry's id is a `public_id` too and
 * would be accepted by both without complaint; what comes back is a 404 and a
 * client history invalidated under an id no client has.
 */
export const clientIdOf = (appointment: ClientAppointment): string | null =>
  appointment.subject === "inquiry" ? null : appointment.clientId || null;

/**
 * The initials a client's avatar disc shows — "SP" for Subham Patil.
 *
 * First and last, skipping the middle: "asha mary rao" is AR, because that is
 * how the name is said. A blank reads as "?" rather than as an empty disc,
 * which would look like something that failed to load.
 */
export const initialsOf = (name: string): string => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";

  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

/**
 * "11:00 am" — the table's second line, on the twelve-hour clock the rest of
 * the app writes times on. See `timeLabel` in `FieldControls`, which this
 * matches; it takes an `HH:MM` string where this takes a moment.
 */
export const appointmentTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";

  const hours = date.getHours();
  const suffix = hours < 12 ? "am" : "pm";
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(shown).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")} ${suffix}`;
};

/** "11:00 AM" — the same time, shouted, as the two strips write it. */
export const appointmentTimeLoud = (iso: string): string =>
  appointmentTime(iso).toUpperCase();

/**
 * "today", "tomorrow", "in 2 days" — how far off an appointment is, in the
 * words the carousel's heading finishes with.
 *
 * Counted in days rather than hours: "in 14 hours" is precise and useless to
 * somebody looking at a week. A day already gone reads as "today" too — the
 * carousel only ever holds appointments still ahead, and the one case that can
 * reach this is an appointment earlier this morning, which is still today.
 */
export const whenLabel = (iso: string, now: Date = new Date()): string => {
  const days = daysBetween(now, new Date(iso));
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
};

/**
 * A meeting URL as something to read: `meet.google.com/abc-defg`.
 *
 * The scheme is stripped because it is noise on a line that is already long,
 * and a trailing slash with it. Anything that will not parse as a URL is shown
 * as it was typed — the box does not insist on a URL, so a practitioner who
 * wrote "Zoom, link to follow" should see that back rather than nothing.
 */
export const linkLabel = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`.replace(/\/$/, "");
  } catch {
    return url;
  }
};

/** Whether this appointment starts on the day `now` falls in. */
export const isToday = (iso: string, now: Date = new Date()): boolean =>
  daysBetween(now, new Date(iso)) === 0;

/**
 * Newest first — the order the table reads in, and the order every list that
 * comes out of the service is put in so nothing downstream has to sort again.
 */
export const byNewest = (a: ClientAppointment, b: ClientAppointment): number =>
  new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime();

/**
 * Soonest first — the order anything about *what is coming* reads in: the
 * carousel's week, and My task's day.
 *
 * The opposite of `byNewest` and not a contradiction of it. A history is read
 * most-recent-first, because the last thing that happened is the thing being
 * looked for; a day is read from the top, because that is the order it will be
 * lived in.
 */
export const bySoonest = (a: ClientAppointment, b: ClientAppointment): number =>
  new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();

/**
 * When the appointment finishes, in milliseconds — its start plus its length,
 * or `null` where the start will not parse.
 *
 * A row the backend stored without an `ends_at` has a length of 0 here (see
 * `toDurationMinutes`), and so finishes the moment it starts. That is the
 * reading that promises least: it is never drawn as still running.
 */
export const endsAtOf = (appointment: ClientAppointment): number | null => {
  const start = new Date(appointment.startsAt).getTime();
  if (Number.isNaN(start)) return null;
  return start + Math.max(appointment.durationMinutes, 0) * 60_000;
};

/**
 * Whether the appointment is happening right now: booked, begun, and not yet
 * finished.
 *
 * The one state the client screen never needed and My task is built around —
 * it is what the timeline lifts out of the day. Note that it overlaps
 * `isAwaitingAnswer`, deliberately: an appointment a minute into its hour is
 * both under way and unanswered, and which of the two a screen draws is that
 * screen's decision. The timeline draws it as running until its time is up and
 * only then asks; the client screen, which has no sense of a day, asks from
 * the first minute. See `taskStateOf`.
 */
export const isInProgress = (
  appointment: ClientAppointment,
  now: Date = new Date()
): boolean => {
  if (!isStandingStatus(appointment.status)) return false;

  const start = new Date(appointment.startsAt).getTime();
  // `runsUntil`, not `endsAtOf`: this and `hasEnded` have to agree about where
  // a session stops, including for a row that arrived without a length, or
  // there would be a stretch of the day in which it was neither running nor
  // finished and the row would quietly fall back to "Upcoming".
  const end = runsUntil(appointment);
  if (Number.isNaN(start) || end === null) return false;

  // From the very start, with no grace: the design shows an appointment as the
  // current one from its own time, and a minute of "Next" at the top of the
  // hour it has just reached would read as a clock that is behind.
  return now.getTime() >= start && now.getTime() < end;
};

/**
 * "02:30 PM - 04:00 PM" — both ends of an appointment, the way the Meeting
 * dialog writes its Time row.
 *
 * The one place in the app that says when an appointment *stops*. Everywhere
 * else writes the start and the length beside it, which is what somebody
 * books; a dialog being read to decide whether to join is being read against a
 * clock, and "until four" is the answer to that question.
 */
export const appointmentTimeRange = (
  appointment: ClientAppointment
): string => {
  const end = endsAtOf(appointment);
  const from = appointmentTimeLoud(appointment.startsAt);
  // No length on record, so there is no far end to write — see `endsAtOf`.
  if (end === null || appointment.durationMinutes <= 0) return from;

  return `${from} - ${appointmentTimeLoud(new Date(end).toISOString())}`;
};

/**
 * The appointments the carousel holds: every one still standing that starts
 * between now and seven days out, soonest first.
 *
 * Soonest first rather than newest first, unlike everything else here. The
 * carousel opens on its first slide, and the first thing somebody should be
 * shown about the week ahead is the next thing in it — the table behind is
 * where a history is read, and that one is newest first.
 *
 * Cancelled ones are left out. A week's worth of things that are not going to
 * happen is not what a strip at the top of a screen is for, and the table
 * below has a tab for them.
 */
export const upcomingSoon = (
  appointments: ClientAppointment[],
  now: Date = new Date()
): ClientAppointment[] =>
  appointments
    .filter((appointment) => {
      // Still booked. One already answered — completed, missed or called
      // off — is a record rather than a reminder.
      if (!isStandingStatus(appointment.status)) return false;
      const start = new Date(appointment.startsAt);
      if (Number.isNaN(start.getTime())) return false;
      // Not over by the clock, so one that finished an hour ago drops off the
      // strip rather than sitting there as "today" — while one that is
      // running right now stays, because that is the single most useful thing
      // the strip can be holding.
      if (hasEnded(appointment, now)) return false;
      return daysBetween(now, start) <= CAROUSEL_WINDOW_DAYS;
    })
    .sort(bySoonest);

/**
 * Today's appointment that still wants something from the practitioner, or
 * `undefined`.
 *
 * What the strip above the tabs is drawn from, and the reason that strip is
 * not simply the first slide of the carousel: the carousel holds the week, and
 * this is about the day.
 *
 * Two kinds qualify, and the strip draws each differently — see
 * `NextAppointmentBanner`. One still ahead is a call to join. One that has
 * started and not been answered is a question to settle, and it stays on the
 * strip until it is: an appointment nobody said anything about is the easiest
 * thing in the day to forget, and the strip is where it will be noticed.
 *
 * The earliest of them, so a morning call that was never marked is settled
 * before an afternoon one is joined.
 */
export const nextToday = (
  appointments: ClientAppointment[],
  now: Date = new Date()
): ClientAppointment | undefined =>
  appointments
    .filter(
      (appointment) =>
        isStandingStatus(appointment.status) &&
        isToday(appointment.startsAt, now)
    )
    .sort(bySoonest)[0];

/**
 * Which tab of the table a row belongs to.
 *
 * Past holds everything whose time has been: the ones answered either way, and
 * the ones still being asked about. That last group is the point of putting
 * them together — Past is where somebody goes to tidy up, and a row awaiting
 * an answer that was filed anywhere else would never be found.
 */
export const inAppointmentTab = (
  appointment: ClientAppointment,
  tab: AppointmentTabId,
  now: Date = new Date()
): boolean => {
  if (tab === "all") return true;

  const state = appointmentState(appointment, now);
  if (tab === "cancelled") return state === "Cancelled";
  // Asked of the record rather than of the state, so a fifth word on the pill
  // could never quietly empty this tab. See `isStandingStatus`.
  if (tab === "upcoming") return isUpcoming(appointment, now);
  return state === "Awaiting" || state === "Completed";
};
